import React, { useState } from 'react';
import { Calendar, Clock, Phone, Mail, User, MessageSquare, CheckCircle, AlertCircle } from 'lucide-react';
import SectionTitle from './SectionTitle';
import config from '../utils/config';
import ReCAPTCHA from 'react-google-recaptcha';

const API_URL = config.url;

export default function AppointmentSection() {
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    date: '',
    time: '',
    purpose: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [errors, setErrors] = useState({});
  const [recaptchaToken, setRecaptchaToken] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    // For phone, allow only digits and max 10 characters
    if (name === 'phone') {
      const digitsOnly = value.replace(/\D/g, '').slice(0, 10);
      setForm(prev => ({ ...prev, [name]: digitsOnly }));
      if (errors[name]) {
        setErrors(prev => ({ ...prev, [name]: '' }));
      }
      setError('');
      setSuccess('');
      return;
    }
    setForm(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    setError('');
    setSuccess('');
  };

  const handleRecaptcha = (token) => {
    setRecaptchaToken(token);
    if (error) setError('');
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!form.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!form.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      newErrors.email = 'कृपया मान्य ईमेल पता दर्ज करें';
    }
    
    if (!form.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\d{10}$/.test(form.phone.trim())) {
      newErrors.phone = 'केवल 10 अंकों का मोबाइल नंबर दर्ज करें (केवल अंक)';
    }
    
    if (!form.date) {
      newErrors.date = 'Date is required';
    } else {
      const selectedDate = new Date(form.date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (selectedDate < today) {
        newErrors.date = 'Please select a future date';
      }
    }
    
    if (!form.time) {
      newErrors.time = 'Time is required';
    }
    if (!recaptchaToken) {
      newErrors.recaptcha = 'कृपया reCAPTCHA पूरा करें';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      const response = await fetch(`${API_URL}/appointments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ...form, recaptchaToken })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setSuccess(data.message);
        setForm({
          name: '',
          email: '',
          phone: '',
          date: '',
          time: '',
          purpose: '',
          message: ''
        });
        setRecaptchaToken('');
        if (window.grecaptcha) {
          window.grecaptcha.reset();
        }
      } else {
        setError(data.error || 'Failed to book appointment');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'success': return 'text-green-600';
      case 'error': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success': return <CheckCircle className="w-5 h-5" />;
      case 'error': return <AlertCircle className="w-5 h-5" />;
      default: return null;
    }
  };

  const toHindiDigits = (str) => str.replace(/[0-9]/g, d => '०१२३४५६७८९'[d]);

  return (
    <section id="appointment" className="py-16 bg-gradient-to-br from-orange-50 to-orange-100" style={{ scrollMarginTop: '120px' }}>
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <SectionTitle>अपॉइंटमेंट बुक करें</SectionTitle>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto hindi-text">
            हमसे मिलने के लिए अपॉइंटमेंट बुक करें। हम आपकी सुविधा के अनुसार समय निर्धारित करेंगे।
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Contact Information */}
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 hindi-text">
                संपर्क जानकारी
              </h3>
              
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-orange-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">फोन नंबर</h4>
                    <a href="tel:+919423014414" className="text-gray-600 hover:text-orange-600 transition-colors">{toHindiDigits('९४२३०१४४१४')}</a>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-orange-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">ईमेल</h4>
                    <a href="mailto:info@sidhisochsidhibaat.com" className="text-gray-600 hover:text-orange-600 transition-colors">info@sidhisochsidhibaat.com</a>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-orange-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">कार्य समय</h4>
                    <p className="text-gray-600">{toHindiDigits('सोमवार - शनिवार: सुबह 9:00 - शाम 6:00')}</p>
                  </div>
                </div>
              </div>
              
              <div className="mt-8 p-6 bg-orange-50 rounded-xl">
                <h4 className="font-semibold text-gray-900 mb-3 hindi-text">
                  महत्वपूर्ण नोट
                </h4>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li>• कृपया अपॉइंटमेंट से {toHindiDigits('15')} मिनट पहले पहुंचें</li>
                  <li>• आवश्यक दस्तावेज साथ लाएं</li>
                  <li>• कोई भी बदलाव {toHindiDigits('24')} घंटे पहले सूचित करें</li>
                </ul>
              </div>
            </div>

            {/* Appointment Form */}
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 hindi-text">
                अपॉइंटमेंट फॉर्म
              </h3>

              {/* Status Messages */}
              {success && (
                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center space-x-3">
                  {getStatusIcon('success')}
                  <p className="text-green-700">{success}</p>
                </div>
              )}
              
              {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-3">
                  {getStatusIcon('error')}
                  <p className="text-red-700">{error}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <User className="w-4 h-4 inline mr-2" />
                      नाम *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors ${errors.name ? 'border-red-300' : 'border-gray-300'} placeholder:text-sm`}
                      placeholder="अपना पूरा नाम दर्ज करें"
                      style={{ fontSize: '0.95rem' }}
                    />
                    {errors.name && (
                      <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Mail className="w-4 h-4 inline mr-2" />
                      ईमेल *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors ${errors.email ? 'border-red-300' : 'border-gray-300'} placeholder:text-sm`}
                      placeholder="your@email.com"
                      style={{ fontSize: '0.95rem' }}
                    />
                    {errors.email && (
                      <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Phone className="w-4 h-4 inline mr-2" />
                      फोन नंबर *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={form.phone}
                      onChange={handleChange}
                      inputMode="numeric"
                      pattern="[0-9]{10}"
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors ${errors.phone ? 'border-red-300' : 'border-gray-300'} placeholder:text-sm`}
                      placeholder="अपना 10 अंकों का मोबाइल नंबर दर्ज करें"
                      style={{ fontSize: '0.95rem' }}
                    />
                    {errors.phone && (
                      <p className="mt-1 text-sm text-red-600">{errors.phone}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Calendar className="w-4 h-4 inline mr-2" />
                      तारीख *
                    </label>
                    <input
                      type="date"
                      name="date"
                      value={form.date}
                      onChange={handleChange}
                      min={new Date().toISOString().split('T')[0]}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors ${errors.date ? 'border-red-300' : 'border-gray-300'} placeholder:text-sm`}
                      style={{ fontSize: '0.95rem' }}
                    />
                    {errors.date && (
                      <p className="mt-1 text-sm text-red-600">{errors.date}</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Clock className="w-4 h-4 inline mr-2" />
                      समय *
                    </label>
                    <input
                      type="time"
                      name="time"
                      value={form.time}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors ${errors.time ? 'border-red-300' : 'border-gray-300'} placeholder:text-sm`}
                      style={{ fontSize: '0.95rem' }}
                    />
                    {errors.time && (
                      <p className="mt-1 text-sm text-red-600">{errors.time}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      उद्देश्य
                    </label>
                    <select
                      name="purpose"
                      value={form.purpose}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors placeholder:text-sm"
                      style={{ fontSize: '0.95rem' }}
                    >
                      <option value="">उद्देश्य चुनें</option>
                      <option value="सामान्य परामर्श">सामान्य परामर्श</option>
                      <option value="कार्यक्रम में भागीदारी">कार्यक्रम में भागीदारी</option>
                      <option value="सहयोग">सहयोग</option>
                      <option value="शिकायत">शिकायत</option>
                      <option value="अन्य">अन्य</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <MessageSquare className="w-4 h-4 inline mr-2" />
                    संदेश
                  </label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors placeholder:text-sm"
                    style={{ fontSize: '0.95rem' }}
                    placeholder="अपना संदेश यहाँ लिखें..."
                  />
                </div>

                <div className="flex justify-center my-2">
                  <ReCAPTCHA
                    sitekey="6LeXpZ8rAAAAAEjIm7QT2dc9ecIfgJYC0U5lvVXv"
                    onChange={handleRecaptcha}
                  />
                </div>
                {errors.recaptcha && (
                  <p className="text-sm text-red-600 text-center">{errors.recaptcha}</p>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-orange-600 hover:bg-orange-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                >
                  {loading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>बुक कर रहे हैं...</span>
                    </>
                  ) : (
                    <>
                      <Calendar className="w-5 h-5" />
                      <span>अपॉइंटमेंट बुक करें</span>
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
} 